import { useEffect, useState } from 'react'
import { useSirr } from '../store'
import { authMode } from '../lib/auth'
import { canPromptInstall, isIos, isStandalone, onInstallChange, promptInstall } from '../lib/install'

const DISMISS_KEY = 'sirr_install_dismissed'

/**
 * Подсказка «установить на экран» для iOS — там нет `beforeinstallprompt`,
 * поэтому установка возможна только руками через «Поделиться». На Android и
 * десктопном Chromium вместо баннера работает кнопка в шапке
 * (`InstallButton`). Закрывается насовсем.
 */
export function InstallBanner() {
  const { t } = useSirr()
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1')
  // Перерисовка, когда Chrome присылает beforeinstallprompt уже после загрузки.
  const [, bump] = useState(0)
  useEffect(() => onInstallChange(() => bump((n) => n + 1)), [])

  if (authMode !== 'pwa' || dismissed || isStandalone()) return null

  const ios = isIos()
  const chromium = canPromptInstall()
  // Где установка идёт в одно нажатие, кнопка уже стоит в шапке — баннер здесь
  // только дублировал бы её. Остаётся iOS: там кнопки нет, нужна инструкция.
  if (!ios || chromium) return null

  const close = () => {
    localStorage.setItem(DISMISS_KEY, '1')
    setDismissed(true)
  }
  const install = async () => {
    if (await promptInstall()) close()
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '10px 14px 0',
        padding: '10px 12px',
        borderRadius: 12,
        border: '1px solid var(--line)',
        background: 'linear-gradient(180deg, rgba(45,26,74,.6), rgba(18,10,31,.5))',
      }}
    >
      <span style={{ color: 'var(--gold)', fontSize: 18, flexShrink: 0 }}>✦</span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 600 }}>{t('installText')}</div>
        {ios && !chromium && (
          <div style={{ fontSize: 11.5, color: 'var(--ink-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
            <IosShareGlyph />
            {t('installIos')}
          </div>
        )}
      </div>

      {/* Кнопка установки — там, где браузер её поддерживает (Chromium). */}
      {chromium && (
        <button
          onClick={() => void install()}
          style={{
            flexShrink: 0,
            background: 'var(--gold-gradient)',
            color: '#1a1020',
            borderRadius: 9,
            padding: '7px 14px',
            fontFamily: 'var(--sans)',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {t('installBtn')}
        </button>
      )}

      <button
        onClick={close}
        aria-label="закрыть"
        style={{ flexShrink: 0, color: 'var(--ink-faint)', fontSize: 18, lineHeight: 1, padding: '0 2px' }}
      >
        ×
      </button>
    </div>
  )
}

/** Иконка «Поделиться» из iOS — квадрат со стрелкой вверх. */
function IosShareGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M12 3v12M12 3l-4 4M12 3l4 4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" strokeLinecap="round" />
    </svg>
  )
}
